import React, { useMemo, useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import DropdownSelect from "../common/DropdownSelect";
import "../../styles/admin/TeacherAssignForm.scss";

/**
 * EditTeacherSubjectsModal
 * 
 * Props:
 *   isOpen                  - boolean
 *   onClose                 - function
 *   onSubmit                - function: called with subject_ids array
 *   teacherName             - string: name of the teacher being edited
 *   subjects                - array: available subjects list [{ id, name }]
 *   initialSelectedSubjects - array: currently assigned subjects [{ id, name }]
 */
const EditTeacherSubjectsModal = ({
  isOpen,
  onClose,
  onSubmit,
  teacherName = "",
  subjects = [],
  initialSelectedSubjects = [],
}) => {
  const [selected, setSelected] = useState([]);

  // Map subjects to dropdown options
  const subjectOptions = useMemo(
    () =>
      (subjects || []).map((s) => ({
        value: s.id ?? s._id ?? s.value ?? s,
        label: s.name ?? s.label ?? String(s),
      })),
    [subjects]
  );

  // Set initial selected options when modal opens or initialSelectedSubjects changes
  useEffect(() => {
    if (isOpen) {
      const initialOptions = (initialSelectedSubjects || []).map((s) => ({
        value: s.id ?? s._id ?? s.value ?? s,
        label: s.name ?? s.label ?? String(s),
      }));
      setSelected(initialOptions);
    }
  }, [isOpen, initialSelectedSubjects]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject_ids = selected.map((item) => item.value);
    onSubmit && onSubmit(subject_ids);
  };

  if (!isOpen) return null;

  return (
    <div className="assign-form-overlay">
      <div className="assign-form-container">
        <div className="assign-form-header">
          <h2 style={{ fontSize: "1.25rem", fontWeight: 600 }}>
            Edit Subjects — <span style={{ color: "var(--color-primary, #007bff)" }}>{teacherName}</span>
          </h2>
          <button
            className="assign-close-button"
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <form className="assign-form" onSubmit={handleSubmit}>
          <div className="assign-form-group">
            <label htmlFor="edit-subjects-select" style={{ marginBottom: "8px", display: "block" }}>
              Assigned Subjects
            </label>
            <DropdownSelect
              inputId="edit-subjects-select"
              isMulti
              options={subjectOptions}
              value={selected}
              onChange={(vals) => setSelected(vals || [])}
              placeholder="Select subjects..."
              searchable
            />
            <span
              style={{
                fontSize: "0.8em",
                color: "var(--color-text-muted, #888)",
                marginTop: "6px",
                display: "block",
              }}
            >
              Selected {selected.length} subject{selected.length !== 1 ? "s" : ""}
            </span>
          </div>

          <div className="assign-form-actions" style={{ marginTop: "24px" }}>
            <button
              type="button"
              className="assign-cancel-button"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="assign-submit-button btn-interactive"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTeacherSubjectsModal;
